import { z } from 'zod';
/**
 * Email OTP verification contract (E-SF05) — the shape US-Shop's CreditPage sends
 * and the shape US-Enrollment-BFF's `POST /bff/email-verification/send` and
 * `POST /bff/email-verification/verify` emit.
 *
 * ── THE ONE RULE THAT GOVERNS THIS FILE: THE CODE GOES DOWN THE WIRE ONCE, ──
 * ── BY EMAIL, AND NEVER OVER HTTP ───────────────────────────────────────────
 * The REQUEST to `/send` carries the address to mail. The RESPONSE carries only
 * WHEN the challenge expires and WHEN a resend becomes available. It deliberately
 * does NOT carry:
 *
 *   - the code (that would make the whole exercise theatre — anyone holding the
 *     HTTP response would hold the proof-of-control it is supposed to demand);
 *   - any signal about whether the address is already known to Accounts, the CRM
 *     or anything else. `/send` answers identically for a brand-new address and
 *     for one that has enrolled ten times, so an anonymous caller cannot turn it
 *     into an account-existence oracle.
 *
 * The BFF's boot-time response-schema guard now forbids `code`/`otpCode`/
 * `verificationCode`/`plaintextCode` on ANY response, so a future edit that
 * "helpfully" echoes the code cannot boot. These schemas are written to already
 * satisfy that guard.
 *
 * ── WHY THIS LIVES IN THE BFF AND NOT IN QUOTATION ──────────────────────────
 * Quotation's E22 verification surface has no dev-token verifier and an
 * unresolved RF-8, so it refuses to run deployed. The OTP is therefore
 * BFF-native: the challenge lives in the funnel session (Redis in prod) and the
 * mail leaves over SMTP from the BFF. Nothing about these schemas assumes that,
 * so a later move upstream is a handler change, not a contract change.
 */
/**
 * `POST /bff/email-verification/send` request.
 *
 * `candidateId` is the OPAQUE, session-scoped premise handle from E-SF03 — the
 * same handle the credit check carries. Requiring it here is not ceremony: it
 * means an anonymous caller cannot use this endpoint as a standalone mail relay
 * without first resolving a real, serviceable Texas premise in their own session.
 *
 * 254 is the RFC 5321 maximum for a forward-path address; anything longer is not
 * an address, it is a payload.
 */
export const EmailVerificationSendRequestSchema = z.object({
    candidateId: z.string().min(1),
    email: z.string().email().max(254),
});
/**
 * `POST /bff/email-verification/send` response.
 *
 * ⚠️ NO `code` FIELD, AND THERE NEVER WILL BE ONE. See the file header.
 *
 * `expiresAt` lets the SPA render an honest countdown instead of guessing a TTL,
 * and `resendAvailableAt` lets it disable "resend" for exactly as long as the
 * server will refuse it — so the button state is derived from the server's policy
 * rather than duplicating it. Both are absolute instants WITH an offset: a bare
 * local timestamp would be interpreted in the browser's zone, which for a Texas
 * funnel viewed from anywhere else is a countdown that is wrong by hours.
 */
export const EmailVerificationSendResponseSchema = z.object({
    status: z.literal('sent'),
    /** ISO-8601 with offset. When the issued code stops being accepted. */
    expiresAt: z.string().datetime({ offset: true }),
    /** ISO-8601 with offset. The earliest instant a resend will not be refused. */
    resendAvailableAt: z.string().datetime({ offset: true }),
});
/**
 * `POST /bff/email-verification/verify` request.
 *
 * The `email` is sent again rather than implied by the session so the server can
 * refuse a code issued for a DIFFERENT address — the case where a customer typed
 * one address, received a code, then edited the field before submitting. Without
 * it, that flow would mark the NEW address verified on the strength of a code
 * mailed to the OLD one.
 *
 * Exactly 6 digits, as a STRING. A number would lose a leading zero, so `012345`
 * would arrive as `12345` and never match.
 */
export const EmailVerificationVerifyRequestSchema = z.object({
    candidateId: z.string().min(1),
    email: z.string().email().max(254),
    code: z.string().regex(/^\d{6}$/u, 'code must be 6 digits'),
});
/**
 * The verify OUTCOME.
 *
 * Four branches, all of them a `200`, because they are all normal answers to a
 * well-formed question rather than protocol errors:
 *
 *   - `verified`  — the code matched; the session now holds a verified address.
 *   - `incorrect` — it did not match; `attemptsRemaining` has been decremented.
 *   - `expired`   — the challenge aged out (request a new one).
 *   - `locked`    — the attempt budget is exhausted (request a new one).
 *
 * `locked` and `expired` are DISTINCT on purpose: they need different words in
 * the UI ("that code has expired" vs "too many attempts"), and collapsing them
 * would make the copy lie in one of the two cases.
 */
export const EmailVerificationOutcome = z.enum(['verified', 'incorrect', 'expired', 'locked']);
/**
 * `POST /bff/email-verification/verify` response.
 *
 * `attemptsRemaining` is `0` for `verified`, `expired` and `locked` — there is
 * nothing left to spend on a challenge that is finished, whichever way it
 * finished. It is deliberately non-negative rather than optional so the SPA never
 * has to decide what `undefined` means.
 */
export const EmailVerificationVerifyResponseSchema = z.object({
    status: EmailVerificationOutcome,
    attemptsRemaining: z.number().int().nonnegative(),
});
/**
 * Email-address normalisation — the ONE arbiter, shared by BOTH sides of the gate.
 *
 * ── WHY THIS IS IN THE CONTRACT PACKAGE AND NOT IN EITHER APP ───────────────
 * Three places compare addresses and they MUST agree byte-for-byte:
 *
 *   1. the BFF's `/email-verification/verify`, which records the proven address;
 *   2. the BFF's `/credit/check`, which looks it up and refuses an unproven one;
 *   3. US-Shop, which decides whether the address currently in the email field is
 *      still the one that was proven (and therefore whether Continue is unblocked).
 *
 * If (3) normalised differently from (1)/(2), the SPA would happily enable Continue
 * for an address the server considers unproven — a guaranteed `400` on the single
 * most consequential PII endpoint in the funnel — or, worse, would keep the button
 * disabled with no visible difference between the two strings for the customer to
 * fix. Either way it fails silently, because a test written with
 * `jane@example.com` on both sides agrees with itself.
 *
 * Mirroring the function in the SPA would put the two copies one careless edit
 * apart. This package exists precisely to be the one-way shared edge, so the
 * normaliser lives here and both sides import it. The tests on either side are
 * parameterised over mixed case and surrounding whitespace, specifically because a
 * lowercase-only test would not have caught a divergence.
 *
 * ⚠️ THE LOCAL PART IS LOWERCASED TOO, WHICH IS TECHNICALLY LOSSY. RFC 5321 says
 * the local part is case-SENSITIVE and only the domain is not, so in principle
 * `Jane@x.com` and `jane@x.com` may be different mailboxes. In practice every
 * mainstream provider folds case, and the alternative here is worse: a customer who
 * verifies `Jane@x.com` and then types `jane@x.com` on the credit step would be told
 * to verify again with no visible difference between the two strings. We take the
 * pragmatic fold, deliberately and in ONE place, rather than shipping a gate that
 * rejects on invisible input.
 */
export function normalizeEmail(raw) {
    return raw.trim().toLowerCase();
}
// ─────────────────────────────────────────────────────────────────────────────
// The OUTSTANDING challenge a returning tab needs to know about (E-SF19 P3)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * An email challenge that is STILL LIVE for this funnel session.
 *
 * ── THE DEFECT THIS CLOSES ─────────────────────────────────────────────────
 * `GET /bff/funnel` carried no pending-challenge field, so after a refresh the SPA
 * had no way to know a valid 10-minute code was already in the customer's inbox.
 * It reverted to "Email me a code", the customer pressed it, and either hit the
 * 60-second resend floor or spent one of only THREE sends per hour — a budget
 * shared with the victim-side recipient cap. A page refresh cost a third of it.
 *
 * ── WHAT IS DELIBERATELY NOT HERE: THE ADDRESS ─────────────────────────────
 * A challenge record holds the recipient from the instant it is issued, and that
 * address is UNPROVEN by definition — `getVerifiedEmail` returns an address only
 * once it has been redeemed, precisely so a refresh can never assert "verified"
 * for one nobody proved. Echoing the pending address here would hand the SPA the
 * one value that distinction exists to withhold. The customer retypes their
 * address (they were mid-form anyway); this only tells the SPA that pressing
 * "send" again would spend a code it does not need.
 *
 * Nor is the CODE here, at any hashing. `response-guard.ts` bans the field name on
 * every route and Pino redacts it at every depth.
 */
export const PendingEmailChallengeSchema = z.object({
    /** ISO-8601 with offset. When the outstanding code stops being accepted. */
    expiresAt: z.string().datetime({ offset: true }),
    /** ISO-8601 with offset. The earliest instant a resend will not be refused. */
    resendAvailableAt: z.string().datetime({ offset: true }),
    /** Verification attempts left on the outstanding code before it locks. */
    attemptsRemaining: z.number().int().nonnegative(),
});
//# sourceMappingURL=email-verification.js.map